// Pure helpers for working with PDOK Locatieserver responses and building the
// nested `Location` map saved on CompagnyMeta / Personas / Adresses.
//
// AI-CONTEXT: See docs/decisions.md#adr-009-pdok-locatieserver-for-organization-address-lookup

// Parses PDOK "POINT(lon lat)" string into { lat, lng }
const parseCentroid = (centroid) => {
    if (typeof centroid !== 'string') return { lat: null, lng: null }
    const match = centroid.match(/POINT\(([-\d.]+)\s+([-\d.]+)\)/)
    if (!match) return { lat: null, lng: null }
    return { lng: parseFloat(match[1]), lat: parseFloat(match[2]) }
}

// PDOK returns gemeentecode as digits ("0363"); CBS standard prefixes "GM"
const formatGemeenteCode = (code) => {
    if (!code) return ''
    return code.startsWith('GM') ? code : `GM${code}`
}

// PDOK exposes huisletter ("a") and huisnummertoevoeging ("bis") separately.
// Concatenate into a single user-facing toevoeging string.
const combineToevoeging = (huisletter, huisnummertoevoeging) => {
    const letter = (huisletter || '').toString()
    const extra = (huisnummertoevoeging || '').toString()
    return `${letter}${extra}`
}

// Builds the Location object stored on Firestore from a PDOK adres doc.
// `input` carries any user-typed values to fall back on when the doc is empty
// (or for the toevoeging the user typed). `status` defaults to 'ok'.
// Caller is responsible for adding `GeocodedAt: serverTimestamp()` before save,
// so this stays a pure function (testable, no firebase import).
const buildLocationFromPdokDoc = (doc = {}, input = {}) => {
    const { lat, lng } = parseCentroid(doc.centroide_ll)
    const docToevoeging = combineToevoeging(doc.huisletter, doc.huisnummertoevoeging)

    return {
        Postcode: doc.postcode || input.postcode || '',
        Huisnummer:
            doc.huisnummer !== undefined && doc.huisnummer !== null
                ? String(doc.huisnummer)
                : input.huisnummer || '',
        HuisnummerToevoeging: docToevoeging || input.toevoeging || '',
        Straatnaam: doc.straatnaam || input.straatnaam || '',
        Woonplaats: doc.woonplaatsnaam || input.woonplaats || '',
        BAGid: doc.adresseerbaarobject_id || doc.nummeraanduiding_id || '',
        Lat: lat,
        Lng: lng,
        BuurtCode: doc.buurtcode || '',
        WijkCode: doc.wijkcode || '',
        GemeenteCode: formatGemeenteCode(doc.gemeentecode),
        Gemeente: doc.gemeentenaam || '',
        Provincie: doc.provincienaam || '',
        GeocodeStatus: input.status || 'ok',
    }
}

// Standard `fl` (field list) parameter for PDOK Locatieserver requests so the
// response carries everything we need to build a Location object.
const PDOK_FL = [
    'straatnaam',
    'huisnummer',
    'huisletter',
    'huisnummertoevoeging',
    'postcode',
    'woonplaatsnaam',
    'gemeentenaam',
    'gemeentecode',
    'provincienaam',
    'buurtcode',
    'wijkcode',
    'adresseerbaarobject_id',
    'nummeraanduiding_id',
    'centroide_ll',
    'weergavenaam',
    'id',
].join(',')

export {
    parseCentroid,
    formatGemeenteCode,
    combineToevoeging,
    buildLocationFromPdokDoc,
    PDOK_FL,
}
